import { IRootStore } from "./root.types";
import { ITextEntity } from "../text-entity.types";

/**
 * Global type declarations for Speech Recognition API
 */
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition;
    webkitSpeechRecognition: new () => SpeechRecognition;
  }

  interface SpeechRecognition extends EventTarget {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    maxAlternatives: number;
    start(): void;
    stop(): void;
    abort(): void;
    onstart: ((this: SpeechRecognition, ev: Event) => unknown) | null;
    onend: ((this: SpeechRecognition, ev: Event) => unknown) | null;
    onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => unknown) | null;
    onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => unknown) | null;
  }

  interface SpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: SpeechRecognitionResultList;
  }

  interface SpeechRecognitionErrorEvent extends Event {
    readonly error: string;
    readonly message: string;
  }

  interface SpeechRecognitionResultList {
    readonly length: number;
    item(index: number): SpeechRecognitionResult;
    [index: number]: SpeechRecognitionResult;
  }

  interface SpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    item(index: number): SpeechRecognitionAlternative;
    [index: number]: SpeechRecognitionAlternative;
  }

  interface SpeechRecognitionAlternative {
    readonly transcript: string;
    readonly confidence: number;
  }
}

/**
 * Enum for word comparison status
 */
export enum WordStatus {
  Pending = 'pending',
  Correct = 'correct',
  Mistake = 'incorrect',
  Include = 'misplaced',
  Missing = 'skipped',
  /** Read with one error's worth of difference: shown amber, counted as read. */
  Near = 'near',
  /**
   * Said, but run into the neighbouring word so the recogniser returned no
   * token for it: ריווח בין הדבקים. Shown amber, counted as read.
   */
  Coarticulated = 'coarticulated',
}

/**
 * Text display modes for speech components
 */
export type TextDisplayMode = "reading" | "byHeart" | "preview" | "result";

/**
 * Interface for word comparison result
 */
export interface IWordCompareResult {
  wordToDisplay: string;
  wordStatus: WordStatus;
}

/**
 * Parameters for base functions related to speech recognition
 */
export interface IWordComparisonParams {
  originalWord: string;
  spokenWord: string;
  originalText?: string[];
  comparedText?: IWordCompareResult[];
  originalWordIndex?: number;
  voicePrecisionLevel?: number;
  isDone?: boolean;
}

/**
 * Store for managing speech recognition, results, scores, spoken texts, etc.
 */
export interface ISpeechRecognitionStore {
  rootStore: IRootStore;
  recognition: AudioWorkletNode | null;
  allSpeechTexts: string;
  speechText: string;
  score: number;
  isListening: boolean;
  isActuallyListening: boolean;
  isInitializing: boolean;
  isPaused: boolean;
  isStarted: boolean;
  isDone: boolean;
  userTextToDisplay: IWordCompareResult[];
  readingResults: unknown;
  readonly getErrorAttemptId: number;

  initRecognition(): void;
  processWordRecognition(correctWords:string[], receivedWords: string[], candidateTolerance: number, sPos:number): number;
  toggleListening(): void;
  doneListening(): void;
  startListening(toClearData: boolean): void;
  stopListening(): void;
  setIsListening(isListening: boolean): void;
  setSpeechText(speechText: string): void;
  setAllSpeechTexts(allSpeechTexts: string): void;
  setScore(score: number): void;
  setIsPaused(isPaused: boolean): void;
  setIsDone(isDone: boolean): void;
  setIsStarted(isStarted: boolean): void;
  setReadingResults(readingResults: unknown): void;
  clearData(): void;
  /** שחרור מלא של המיקרופון (יציאה ממצב קריאה) - בין פסוקים ברצף הוא נשאר פתוח */
  releaseMicrophone(): Promise<void>;
  /** פתיחה מוקדמת כשהפסוק מוצג, לפני הכפתור הירוק. נסגר מעצמו אחרי 30 שניות בלי לחיצה */
  prepareToListen(): Promise<void>;
  calculateAndSendRank(): Promise<unknown>;
}

/**
 * מייצג מילה אחת בקטע הקריאה
 */
export interface IReadingWord {
  /** האינדקס של המילה בקטע הקריאה */
  index: number;
  
  /** המילה הנקייה (ללא סימני פיסוק) */
  cleanWord: string;
  
  /** הסטטוס הנוכחי של המילה */
  status: WordStatus;
  
  /** רשימת הניסיונות השגויים שנאמרו עבור המילה הזו */
  attemptedWords: string[];
  
  /** האם זו המילה הנוכחית שהמשתמש צריך לקרוא */
  isCurrentWord: boolean;
  
  /** מזהה ייחודי לאנימציית שגיאה */
  errorAttemptId?: number;
}

/**
 * מייצג קטע מלא עם כל המילים והמצב הנוכחי
 */
export interface IReadingSession {
  /** מערך כל המילים בקטע הקריאה */
  words: IReadingWord[];
  
  /** האינדקס של המילה הנוכחית */
  currentWordIndex: number;
  
  /** הטקסט שנאמר עד כה (מילים שעברו בהצלחה) */
  spokenText: string;
  
  /** האם הקריאה הושלמה */
  isCompleted: boolean;
  
  /** מזהה ייחודי של הקטע המקורי */
  textEntityId: number;
  
  /** מספר השגיאות הנוכחי למילה הנוכחית */
  currentWordErrorCount: number;
  
  /** מזהה ייחודי לאנימציית השגיאה הנוכחית */
  errorAttemptId: number;
  
  /** המילה שנקראה בניסיון הנוכחי */
  currentAttemptedWord: string;
}

/**
 * פעולות שניתן לבצע על קטע הקריאה
 */
export interface IReadingSessionActions {
  /** עדכון סטטוס מילה לאחר ניסיון קריאה */
  updateWordStatus(wordIndex: number, spokenWord: string, status: WordStatus): void;
  
  /** מעבר למילה הבאה לאחר הצלחה */
  advanceToNextWord(): void;
  
  /** סימון מילה כנכשלת לאחר 3 ניסיונות */
  markWordAsFailed(wordIndex: number): void;
  
  /** איפוס מילה אחרונה */
  removeLastWord(): void;
  
  /** איפוס כל הטקסט */
  resetSession(): void;
  
  /** יצירת session חדש מ-textEntity */
  initializeFromTextEntity(textEntity: ITextEntity): void;
}

/**
 * סטטיסטיקות על הקריאה
 */
export interface IReadingSessionStats {
  /** כמות מילים שנקראו נכון */
  correctWords: number;
  
  /** כמות מילים שנכשלו */
  failedWords: number;
  
  /** כמות מילים שממתינות */
  pendingWords: number;

  /** מילים שנקראו כמעט נכון (כלולות ב-correctWords) */
  nearWords?: number;

  /** מילים שנבלעו במילה הסמוכה (כלולות ב-correctWords) */
  coarticulatedWords?: number;
  
  /** אחוז הדיוק */
  accuracyPercentage: number;
}

/**
 * פונקציה ליצירת session חדש מ-textEntity
 * @param textEntity הישות טקסט ליצירת ה-session ממנה
 * @returns session חדש מאותחל
 */
export const createReadingSessionFromTextEntity = (textEntity: ITextEntity): IReadingSession => {
  const words: IReadingWord[] = textEntity.cleanWordsArray.map((cleanWord, index) => ({
    index,
    cleanWord,
    status: WordStatus.Pending,
    attemptedWords: [],
    isCurrentWord: index === 0,
    errorAttemptId: undefined,
  }));

  return {
    words,
    currentWordIndex: 0,
    spokenText: '',
    isCompleted: false,
    textEntityId: textEntity.id,
    currentWordErrorCount: 0,
    errorAttemptId: 0,
    currentAttemptedWord: '',
  };
}; 